/**
 * Cart slice — Redux Toolkit state + thunks for the storefront shopping
 * cart.
 *
 * State shape
 * -----------
 * ```ts
 * {
 *   id: number | null,
 *   expiresAt: string | null,
 *   items: CartItemDTO[],
 *   coupon: CouponDTO | null,
 *   subtotal: number,
 *   discount_total: number,
 *   shipping_estimate: number | null,
 *   tax: number | null,
 *   total: number,
 *   isLoading: boolean,
 *   error: CartError | null,
 * }
 * ```
 *
 * Invariants
 * ----------
 * - The server is authoritative for every total (`subtotal`,
 *   `discount_total`, `tax`, `total`). The slice never derives them
 *   client-side. The only locally-computed values are
 *   {@link selectCartIsEmpty} (boolean) and
 *   {@link selectCartItemCount} (`items.length`).
 * - Every mutating thunk calls the server and replaces state with the
 *   server response. We do NOT optimistically mutate `items` or totals
 *   on `removeItem` / `updateItem` — `isLoading` is the UI's signal to
 *   disable the relevant control while the request is in flight.
 * - `fetchCart` carries a `condition` guard so multiple components
 *   dispatching it on mount don't duplicate the request — the second
 *   dispatch resolves to a no-op when `isLoading === true`.
 * - On every state transition that changes `items.length`, the cart
 *   slice's listener middleware writes the new count to `localStorage`
 *   under `dfq_cart_count` so the header badge (SF-CART-012) can show
 *   a non-zero count on hard refresh before `fetchCart` resolves.
 *
 * Per `.claude/rules/frontend-data-flow.md`:
 *
 *  - Capsules MUST NOT import this slice. Containers do.
 *  - Errors caught from the api-client are `CartApiError` instances; the
 *    thunk's `rejectWithValue` projects them onto a {@link CartError}
 *    shape that keeps the envelope's `message`, `severity` and field
 *    `errors` so views can show specific text without parsing strings.
 *
 * Pattern reference: `authSlice` (SF-AUTH-005) — co-located thunks,
 * typed state interface, `extraReducers` builder pattern, and named
 * selector exports.
 *
 * SF-CART-001.
 */
import {
  createAsyncThunk,
  createListenerMiddleware,
  createSelector,
  createSlice,
  isAnyOf,
} from '@reduxjs/toolkit';

import * as cartApi from '@dfq/shared/api/cart';
import { CartApiError } from '@dfq/shared/api/cart';
import type {
  CartDTO,
  CartError,
  CartItemDTO,
  CartItemInput,
  CartItemUpdate,
  CouponDTO,
} from '@dfq/shared/types/cart';

/**
 * Stable localStorage key consumed by the header badge (SF-CART-012).
 * Stored as a stringified integer; readers MUST tolerate missing /
 * malformed values (the badge falls back to 0 on parse failure).
 */
export const CART_COUNT_STORAGE_KEY = 'dfq_cart_count';

// ----------------------------------------------------------------------------
// State
// ----------------------------------------------------------------------------

export interface CartState {
  /** Server-assigned cart id; null until first fetch / mutation resolves. */
  id: number | null;
  /** Cart expiry timestamp (server-authoritative). Display-only here. */
  expiresAt: string | null;
  items: CartItemDTO[];
  coupon: CouponDTO | null;
  subtotal: number;
  discount_total: number;
  shipping_estimate: number | null;
  tax: number | null;
  total: number;
  isLoading: boolean;
  error: CartError | null;
}

const initialState: CartState = {
  id: null,
  expiresAt: null,
  items: [],
  coupon: null,
  subtotal: 0,
  discount_total: 0,
  shipping_estimate: null,
  tax: null,
  total: 0,
  isLoading: false,
  error: null,
};

// ----------------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------------

/**
 * Normalise a {@link CartDTO} payload onto the slice's mutable state.
 * Used by every fulfilled reducer so partial server responses (e.g.
 * the add-item response that omits `expires_at`) merge with sensible
 * defaults and keep the state shape stable.
 */
function applyCartToState(state: CartState, cart: CartDTO): void {
  state.id = cart.id ?? null;
  state.expiresAt = cart.expires_at ?? null;
  state.items = cart.items ?? [];
  state.coupon = cart.coupon ?? null;
  state.subtotal = cart.subtotal ?? 0;
  state.discount_total = cart.discount_total ?? 0;
  state.shipping_estimate = cart.shipping_estimate ?? null;
  state.tax = cart.tax ?? null;
  state.total = cart.total ?? 0;
}

/**
 * Project an unknown thrown value onto the slice's structured
 * {@link CartError} payload. Always returns a populated `error_code` so
 * UI branches can switch without checking for `undefined`; `errors` and
 * `severity` are carried over only when the envelope had them.
 */
function rejectionFromUnknown(err: unknown): CartError {
  if (err instanceof CartApiError) {
    return {
      error_code: err.error_code,
      message: err.message,
      status: err.status,
      ...(err.errors !== undefined && { errors: err.errors }),
      ...(err.severity !== undefined && { severity: err.severity }),
    };
  }
  if (err instanceof Error) {
    return {
      error_code: 'NETWORK_ERROR',
      message: err.message,
    };
  }
  return {
    error_code: 'UNKNOWN',
    message: 'Unknown cart error',
  };
}

// ----------------------------------------------------------------------------
// Thunks
// ----------------------------------------------------------------------------

/**
 * Minimal slice of root state the thunk's `condition` guard reads.
 * Avoids a circular import through the root barrel.
 */
interface CartRootStateSlice {
  cart: CartState;
}

/**
 * `GET /api/v1/cart` — fetch the authenticated user's cart.
 *
 * Carries a `condition` guard: if `isLoading` is already `true` (because
 * another component on the same page already dispatched), the second
 * dispatch resolves to a no-op so we don't duplicate the in-flight
 * request.
 */
export const fetchCart = createAsyncThunk<
  CartDTO,
  void,
  { state: CartRootStateSlice; rejectValue: CartError }
>('cart/fetchCart', async (_, { rejectWithValue }) => {
  try {
    return await cartApi.fetchCartApi();
  } catch (err) {
    return rejectWithValue(rejectionFromUnknown(err));
  }
}, {
  condition: (_arg, { getState }) => {
    if (getState().cart.isLoading) {
      return false;
    }
    return true;
  },
});

/**
 * `POST /api/v1/cart` — add a configured line item. Replaces the local
 * cart with the server-recalculated cart on success.
 */
export const addItem = createAsyncThunk<
  CartDTO,
  CartItemInput,
  { rejectValue: CartError }
>('cart/addItem', async (payload, { rejectWithValue }) => {
  try {
    return await cartApi.addCartItemApiV2(payload);
  } catch (err) {
    return rejectWithValue(rejectionFromUnknown(err));
  }
});

export interface UpdateItemArg {
  itemId: number;
  /** Partial update payload — currently `quantity_sqft`. */
  patch: CartItemUpdate;
}

/**
 * `PUT /api/v1/cart/:itemId` — update a single line item's quantity
 * (or, post-SF-CART-011, attached artwork). Returns the full updated
 * cart so totals stay server-authoritative.
 */
export const updateItem = createAsyncThunk<
  CartDTO,
  UpdateItemArg,
  { rejectValue: CartError }
>('cart/updateItem', async ({ itemId, patch }, { rejectWithValue }) => {
  try {
    return await cartApi.updateCartItemApi(itemId, patch);
  } catch (err) {
    return rejectWithValue(rejectionFromUnknown(err));
  }
});

/**
 * `DELETE /api/v1/cart/:itemId` — remove a line item. The full server
 * response (with the line absent) replaces local state.
 */
export const removeItem = createAsyncThunk<
  CartDTO,
  number,
  { rejectValue: CartError }
>('cart/removeItem', async (itemId, { rejectWithValue }) => {
  try {
    return await cartApi.removeCartItemApi(itemId);
  } catch (err) {
    return rejectWithValue(rejectionFromUnknown(err));
  }
});

/**
 * `POST /api/v1/cart/coupon` — apply a coupon code. A 422 rejection carries
 * the platform's field message in `errors.code` alongside the envelope
 * `message`; a 401 (guest or expired session) carries `status: 401`.
 */
export const applyCoupon = createAsyncThunk<
  CartDTO,
  string,
  { rejectValue: CartError }
>('cart/applyCoupon', async (code, { rejectWithValue }) => {
  try {
    return await cartApi.applyCartCouponApi(code);
  } catch (err) {
    return rejectWithValue(rejectionFromUnknown(err));
  }
});

/**
 * `DELETE /api/v1/cart/coupon` — remove the currently applied coupon.
 */
export const removeCoupon = createAsyncThunk<
  CartDTO,
  void,
  { rejectValue: CartError }
>('cart/removeCoupon', async (_, { rejectWithValue }) => {
  try {
    return await cartApi.removeCartCouponApi();
  } catch (err) {
    return rejectWithValue(rejectionFromUnknown(err));
  }
});

// ----------------------------------------------------------------------------
// Slice
// ----------------------------------------------------------------------------

const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    /**
     * Reset the cart slice to its initial shape. Dispatched by the
     * checkout confirmation flow (SF-CART-009) after a successful order
     * placement so the freshly-emptied cart no longer lingers in state.
     */
    clearCart() {
      return initialState;
    },
    /**
     * Clear the transient `error` field — called by the UI after a
     * coupon-error toast is dismissed or the customer navigates away.
     */
    clearCartError(state) {
      state.error = null;
    },
  },
  extraReducers(builder) {
    builder
      // -- fetchCart ----------------------------------------------------
      .addCase(fetchCart.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCart.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        applyCartToState(state, action.payload);
      })
      .addCase(fetchCart.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          (action.payload as CartError | undefined) ??
          { error_code: 'UNKNOWN', message: action.error.message ?? 'Failed to fetch cart' };
      })
      // -- addItem ------------------------------------------------------
      .addCase(addItem.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addItem.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        applyCartToState(state, action.payload);
      })
      .addCase(addItem.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          (action.payload as CartError | undefined) ??
          { error_code: 'UNKNOWN', message: action.error.message ?? 'Failed to add item' };
      })
      // -- updateItem ---------------------------------------------------
      .addCase(updateItem.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateItem.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        applyCartToState(state, action.payload);
      })
      .addCase(updateItem.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          (action.payload as CartError | undefined) ??
          { error_code: 'UNKNOWN', message: action.error.message ?? 'Failed to update item' };
      })
      // -- removeItem ---------------------------------------------------
      .addCase(removeItem.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(removeItem.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        applyCartToState(state, action.payload);
      })
      .addCase(removeItem.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          (action.payload as CartError | undefined) ??
          { error_code: 'UNKNOWN', message: action.error.message ?? 'Failed to remove item' };
      })
      // -- applyCoupon --------------------------------------------------
      .addCase(applyCoupon.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(applyCoupon.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        applyCartToState(state, action.payload);
      })
      .addCase(applyCoupon.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          (action.payload as CartError | undefined) ??
          { error_code: 'UNKNOWN', message: action.error.message ?? 'Failed to apply coupon' };
      })
      // -- removeCoupon -------------------------------------------------
      .addCase(removeCoupon.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(removeCoupon.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        applyCartToState(state, action.payload);
      })
      .addCase(removeCoupon.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          (action.payload as CartError | undefined) ??
          { error_code: 'UNKNOWN', message: action.error.message ?? 'Failed to remove coupon' };
      });
  },
});

// ----------------------------------------------------------------------------
// localStorage listener middleware
//
// Watches every action that can change `items.length` and writes the
// updated count to `dfq_cart_count`. Per the ticket's Technical Notes
// we do NOT persist the full cart — contents are always re-fetched from
// the server on app boot. The badge component reads the count
// synchronously on render so a hard refresh shows a non-zero badge
// before `fetchCart` resolves.
// ----------------------------------------------------------------------------

export const cartListenerMiddleware = createListenerMiddleware();

cartListenerMiddleware.startListening({
  matcher: isAnyOf(
    fetchCart.fulfilled,
    addItem.fulfilled,
    updateItem.fulfilled,
    removeItem.fulfilled,
    applyCoupon.fulfilled,
    removeCoupon.fulfilled,
    cartSlice.actions.clearCart,
  ),
  effect: (_action, listenerApi) => {
    const state = listenerApi.getState() as CartRootStateSlice;
    const count = state.cart.items.length;
    writeCartCountToStorage(count);
  },
});

function writeCartCountToStorage(count: number): void {
  if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(CART_COUNT_STORAGE_KEY, String(count));
  } catch {
    // localStorage can throw in private-browsing / quota-exceeded modes.
    // The badge is best-effort; a throw here must not break the slice.
  }
}

// ----------------------------------------------------------------------------
// Selectors
// ----------------------------------------------------------------------------

const selectCartState = (state: CartRootStateSlice): CartState => state.cart;

/**
 * Returns the current cart line items. Memoised — the same array
 * reference is returned across renders when the underlying slot is
 * unchanged.
 */
export const selectCartItems = createSelector(
  [selectCartState],
  (s): CartItemDTO[] => s.items,
);

/**
 * Returns the server-computed `total`. Never derived client-side.
 */
export const selectCartTotal = createSelector(
  [selectCartState],
  (s): number => s.total,
);

/**
 * Server-computed money fields, all in integer cents. `shipping_estimate` and
 * `tax` are null until an address makes them calculable.
 */
export const selectCartSubtotal = createSelector(
  [selectCartState],
  (s): number => s.subtotal,
);

export const selectCartShippingEstimate = createSelector(
  [selectCartState],
  (s): number | null => s.shipping_estimate,
);

export const selectCartTax = createSelector(
  [selectCartState],
  (s): number | null => s.tax,
);

/**
 * Number of distinct line items in the cart. The header badge consumes
 * this value (and falls back to the localStorage cache before
 * `fetchCart` resolves).
 */
export const selectCartItemCount = createSelector(
  [selectCartState],
  (s): number => s.items.length,
);

/**
 * The currently applied coupon, or `null` when none.
 */
export const selectCartCoupon = createSelector(
  [selectCartState],
  (s): CouponDTO | null => s.coupon,
);

/**
 * `true` when the cart has zero line items.
 */
export const selectCartIsEmpty = createSelector(
  [selectCartState],
  (s): boolean => s.items.length === 0,
);

/**
 * Loading flag — true while any cart thunk is in flight. Containers
 * use this to disable controls.
 */
export const selectCartIsLoading = createSelector(
  [selectCartState],
  (s): boolean => s.isLoading,
);

/**
 * Last cart error, or `null` when none.
 */
export const selectCartError = createSelector(
  [selectCartState],
  (s): CartError | null => s.error,
);

// ----------------------------------------------------------------------------
// Exports
// ----------------------------------------------------------------------------

export const { clearCart, clearCartError } = cartSlice.actions;

export { cartSlice };
export const cartReducer = cartSlice.reducer;
export default cartReducer;
